/**
 * Re-export from shared package — service migrated to @watchlist/shared
 *
 * The shared version uses Firestore for persistence with async methods.
 * This wrapper provides sync-compatible methods for backward compat,
 * using the in-memory cache for reads.
 */
import { favoritePeopleService as sharedService, type FavoritePerson } from '@watchlist/shared';

export type { FavoritePerson };

/**
 * Web-specific wrapper that exposes sync methods for backward compat.
 * Delegates to the shared async service but uses sync cache for reads.
 */
class WebFavoritePeopleService {
  clearInMemoryCache(): void {
    sharedService.clearInMemoryCache();
  }

  subscribe(listener: (favorites: FavoritePerson[]) => void, userId?: string): () => void {
    return sharedService.subscribe(listener, userId);
  }

  getFavorites(userId?: string): FavoritePerson[] {
    return sharedService.getFavoritesSync(userId);
  }

  /**
   * Async version that fetches from Firestore
   * Use this when you need fresh data from the server
   */
  async getFavoritesAsync(userId?: string): Promise<FavoritePerson[]> {
    return sharedService.getFavorites(userId);
  }

  isFavorite(personId: string, userId?: string): boolean {
    return sharedService.isFavoriteSync(personId, userId);
  }

  addFavorite(person: FavoritePerson, userId?: string): void {
    sharedService.addFavorite(person, userId);
  }

  removeFavorite(personId: string, userId?: string): void {
    sharedService.removeFavorite(personId, userId);
  }

  toggleFavorite(person: FavoritePerson, userId?: string): boolean {
    const isFav = sharedService.isFavoriteSync(person.id, userId);
    if (isFav) {
      sharedService.removeFavorite(person.id, userId);
      return false;
    } else {
      sharedService.addFavorite(person, userId);
      return true;
    }
  }

  clearAll(userId?: string): void {
    sharedService.clearAll(userId);
  }

  /**
   * Migrate local storage favorites to Firestore
   * Call this when user logs in to ensure their favorites are persisted
   */
  async migrateToFirestore(userId: string): Promise<void> {
    return sharedService.migrateToFirestore(userId);
  }

  /**
   * @deprecated Use migrateToFirestore instead
   */
  migrateToUserSpecificKey(userId: string): void {
    // Redirect to new async migration
    sharedService.migrateToFirestore(userId);
  }
}

export const favoritePeopleService = new WebFavoritePeopleService();
